/**
 * Kliendiotsing: one page for the prospect funnel, one tab per step.
 *
 * The shell and nothing else. It draws the page head, the tab strip and an
 * inner router, and mounts the five existing section bodies - plus the lead
 * watch with its incident list as the incidents tab - at the segments
 * `steps.ts` lists. Anything a tab needs to know about its own rows belongs in
 * that tab's folder, not here: a shell that starts carrying tab logic is how
 * the next 1200-line file is born (AGENTS.md §4).
 *
 * ONE HEAD FOR THE WHOLE PAGE. The title is the page's, "Kliendiotsing", and
 * the sentence under it is the open tab's (`introKeyFor` in `steps.ts`), so the
 * head still says what the list below is for - or, on one company's brain
 * page, what that page is, since the list's sentence asks the operator to open
 * a company they already have open. The bodies draw no head of their
 * own: each one's actions (the CSV export, the bulk scan, the refresh, the
 * brain's back link) sit in that tab's toolbar, next to the list they act on.
 * Two stacked titles on one screen was the price of mounting the old pages
 * unchanged, and the rail no longer jumps as the head comes and goes.
 *
 * WHY TABS AND NOT ONE LONG PAGE. The old pages argued for being separate, and
 * the argument was right about the part that matters: each step answers its
 * own question ("what is this site", "who do we call", "who did I promise to
 * ring back") and must not share a filter bar with the others. Routed tabs keep
 * that - only the open tab is mounted, and each keeps its own toolbar - while
 * the sidebar goes from five rows to two and the order of the work is on screen.
 *
 * WHY `TabRail as="nav"` AND NOT `PillTabs`. These tabs are places, not filter
 * state: each is a real link (middle-click, copy address, back button) and
 * `NavLink` marks the open one with `aria-current="page"`. `PillTabs` is a
 * value/onChange control, and the brain tab already draws one as its status
 * filter; two identical pill rows stacked on each other would read as one
 * control. This is the same strip Settings uses.
 *
 * NOT REMOUNTED ON A TAB SWITCH. `CrmLayout` keys the page on the first path
 * segment, which is `prospects` on every tab, so moving between tabs or writing
 * `?url=` swaps the inner route without replaying the page's entrance or
 * rebuilding the shell.
 */
import type { ReactNode } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import { PageHead, TabRail } from "../../ui";
import { SiteAuditsSection } from "../site-audits";
import { SiteScannerSection } from "../site-scanner";
import { ScanLeadsSection } from "../scan-leads";
import { BrainSection } from "../brain";
import { BrainDueSection } from "../brain-due";
import { WatchPanel } from "../scan-leads/watch/WatchPanel";
import type { T } from "../site-scanner/types";
import { HOME_STEP, STEPS, introKeyFor, stepPath, type StepKey } from "./steps";

interface BodyProps {
  user: StaffUser;
  canManage: boolean;
  t: T;
}

/**
 * What each tab mounts. A record over `StepKey` rather than a list, so a step
 * added to `steps.ts` without a body is a type error instead of a tab that
 * opens onto the home redirect.
 *
 * The incidents tab is `scan-leads/watch/WatchPanel`: the lead watch's strip
 * over the open incidents. It left the leads tab in plan step 3, together with
 * the scan autopilot and the do-not-scan list, which the scans body now draws.
 */
const BODIES: Record<StepKey, (props: BodyProps) => ReactNode> = {
  inbound: ({ user }) => <SiteAuditsSection user={user} />,
  scans: ({ user }) => <SiteScannerSection user={user} />,
  leads: ({ user }) => <ScanLeadsSection user={user} />,
  brain: ({ user }) => <BrainSection user={user} />,
  incidents: ({ canManage, t }) => <WatchPanel canManage={canManage} t={t} />,
  "follow-up": ({ user }) => <BrainDueSection user={user} />,
};

/** The quiet tab pill, the same one Settings wears: a row of places read as a
 *  strip, not as six raised buttons. */
const tabClass = ({ isActive }: { isActive: boolean }) => cn("inline-flex items-center", isActive && "is-on");

export function ProspectingSection({ user }: { user: StaffUser }) {
  const t = useT();
  const { pathname, search } = useLocation();
  // The same key every write on these tabs answers to on the server; reading
  // is `view:ariregister`, which is what put this page in the sidebar at all.
  const canManage = userCanPlatform(user, "manage:ariregister");
  // Kept on the way to the home tab, so `/prospects?q=naide.ee` still searches.
  const home = `${stepPath(HOME_STEP)}${search}`;
  // Null only for the one frame before an unknown address is sent home; the
  // head keeps its title then and simply has no sentence.
  const introKey = introKeyFor(pathname);

  return (
    <>
      {/* The sentence keeps two lines of room (three on a phone) whether it
          needs them or not. The sentences differ in length from tab to tab,
          and a head that grew and shrank with them moved the tab strip under
          the pointer on every switch - the one thing the single head is for. */}
      <PageHead
        title={t("prospecting.title")}
        sub={introKey ? t(introKey) : undefined}
        className="[&_.crm-sub]:min-h-[2lh] max-sm:[&_.crm-sub]:min-h-[3lh]"
      />

      <TabRail as="nav" className="mb-5" tabsClassName="crm-tabs-quiet" label={t("prospecting.title")}>
        {STEPS.map((s) => (
          <NavLink key={s.key} to={stepPath(s.key)} className={tabClass}>
            {t(s.labelKey)}
          </NavLink>
        ))}
      </TabRail>

      <Routes>
        <Route index element={<Navigate to={home} replace />} />
        {STEPS.map((s) => {
          const Body = BODIES[s.key];
          return (
            <Route
              key={s.key}
              path={s.nested ? `${s.key}/*` : s.key}
              element={<Body user={user} canManage={canManage} t={t} />}
            />
          );
        })}
        <Route path="*" element={<Navigate to={home} replace />} />
      </Routes>
    </>
  );
}
